import React from 'react';
import { Clock, Ban, AlertTriangle, Zap, Target, RefreshCw } from 'lucide-react';

const GhlPainPoints: React.FC = () => {
  return (
    <>
      {/* THE PROBLEM */}
      <section id="why-ghl" className="py-20 bg-slate-950 border-b border-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-white mb-4">Your GHL Campaigns Are Only as Good as Your Data</h2>
            <p className="mt-4 text-slate-400 max-w-2xl mx-auto">
              You have the perfect GHL workflow built. Snapshots, automations, email sequences, SMS follow-ups.
              But if your contact list is garbage, none of it matters.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="p-8 rounded-xl bg-gradient-to-br from-slate-900 to-slate-900 border border-slate-800 relative overflow-hidden">
              <div className="absolute top-0 right-0 p-4 opacity-10">
                <Clock className="w-24 h-24 text-rose-500" />
              </div>
              <h3 className="text-lg font-bold text-white mb-4 flex items-center">
                <Clock className="w-5 h-5 text-rose-500 mr-2" /> Hours Wasted Scraping
              </h3>
              <p className="text-slate-400 text-sm leading-relaxed">
                You are spending hours on state licensing board websites, copy-pasting data into spreadsheets,
                and cleaning it up before you can even import to GHL. That is time you could spend closing deals.
              </p>
            </div>

            <div className="p-8 rounded-xl bg-gradient-to-br from-slate-900 to-slate-900 border border-slate-800 relative overflow-hidden">
              <div className="absolute top-0 right-0 p-4 opacity-10">
                <Ban className="w-24 h-24 text-amber-500" />
              </div>
              <h3 className="text-lg font-bold text-white mb-4 flex items-center">
                <Ban className="w-5 h-5 text-amber-500 mr-2" /> Stale Data, Bad Deliverability
              </h3>
              <p className="text-slate-400 text-sm leading-relaxed">
                Bought a list from a broker? Half those emails bounce, numbers are disconnected,
                and you are burning your GHL sending reputation. Stale data kills campaigns.
              </p>
            </div>

            <div className="p-8 rounded-xl bg-gradient-to-br from-slate-900 to-slate-900 border border-slate-800 relative overflow-hidden">
              <div className="absolute top-0 right-0 p-4 opacity-10">
                <AlertTriangle className="w-24 h-24 text-indigo-500" />
              </div>
              <h3 className="text-lg font-bold text-white mb-4 flex items-center">
                <AlertTriangle className="w-5 h-5 text-indigo-500 mr-2" /> No Way to Verify Status
              </h3>
              <p className="text-slate-400 text-sm leading-relaxed">
                You are sending campaigns to people who lost their license, retired, or moved. Without
                real-time verification, you waste ad spend and damage your brand.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* THE SOLUTION */}
      <section className="py-20 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-white mb-4">ProGeoData Solves This</h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              We aggregate, verify, and enrich professional license data from official state boards daily.
              Export a clean CSV, import to GHL, and launch your campaign in minutes.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            <div className="group p-6 rounded-2xl bg-slate-900/50 border border-slate-800 hover:border-emerald-500/50 transition-all duration-300">
              <div className="w-12 h-12 rounded-lg bg-emerald-500/10 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Zap className="w-6 h-6 text-emerald-400" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Instant GHL-Ready Lists</h3>
              <p className="text-slate-400 leading-relaxed">
                Search by profession, state, city, or ZIP code. Export as CSV with columns that map
                directly to GHL contact fields. First Name, Last Name, Email, Phone, Address. Done.
              </p>
            </div>

            <div className="group p-6 rounded-2xl bg-slate-900/50 border border-slate-800 hover:border-sky-500/50 transition-all duration-300">
              <div className="w-12 h-12 rounded-lg bg-sky-500/10 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Target className="w-6 h-6 text-sky-400" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Precision Targeting</h3>
              <p className="text-slate-400 leading-relaxed">
                Target dentists in Miami, plumbers in Dallas, real estate agents in Phoenix, or
                electricians in a specific ZIP code. Hyper-local campaigns that convert.
              </p>
            </div>

            <div className="group p-6 rounded-2xl bg-slate-900/50 border border-slate-800 hover:border-indigo-500/50 transition-all duration-300">
              <div className="w-12 h-12 rounded-lg bg-indigo-500/10 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <RefreshCw className="w-6 h-6 text-indigo-400" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Always Fresh Data</h3>
              <p className="text-slate-400 leading-relaxed">
                Our data syncs with official state licensing boards daily. When you export today,
                you are getting today's data. Not last quarter's recycled list.
              </p>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default GhlPainPoints;
